/** Admin: usage views and export (spec section 13, 17). */
import { z } from 'zod';
import { AccountIdSchema, AiKeyIdSchema, UserIdSchema } from '../../ids.js';
import { CountSchema, ExportFormatSchema, ExportResultSchema, IsoDateTimeSchema, MicroUsdSchema, TimeRangeQuerySchema, defineRoute } from '../common.js';
import { UsagePageSchema, UsageQuerySchema, UsageSummarySchema } from '../usage.js';

export const AdminUsageQuerySchema = UsageQuerySchema.extend({ aiKeyId: AiKeyIdSchema.optional() });

export const UsageByUserRowSchema = z.object({
  userId: UserIdSchema,
  displayName: z.string(),
  email: z.string(),
  requestCount: CountSchema,
  recordCount: CountSchema,
  inputTokens: CountSchema,
  outputTokens: CountSchema,
  cachedTokens: CountSchema,
  estimatedCostMicroUsd: MicroUsdSchema,
  actualCostMicroUsd: MicroUsdSchema.nullable(),
  estimatedCount: CountSchema,
  reconciledCount: CountSchema,
  unknownCount: CountSchema,
  lastUsedAt: IsoDateTimeSchema.nullable(),
});

export const UsageByAccountRowSchema = z.object({
  accountId: AccountIdSchema.nullable(),
  displayName: z.string(),
  requestCount: CountSchema,
  estimatedCostMicroUsd: MicroUsdSchema,
});

/** Operational counts derived from audit events, shown next to spend. */
export const ActivityCountsSchema = z.object({
  uniqueAccounts: CountSchema,
  syncAttempts: CountSchema,
  syncCompletions: CountSchema,
  failedDatasets: CountSchema,
  rateLimitWaits: CountSchema,
  reportsGenerated: CountSchema,
  exports: CountSchema,
  gmailDrafts: CountSchema,
  gmailSends: CountSchema,
  rumiRequests: CountSchema,
  rumiTokens: CountSchema,
});
export type ActivityCounts = z.infer<typeof ActivityCountsSchema>;

export const AdminUsageSummaryQuerySchema = AdminUsageQuerySchema.omit({ cursor: true, limit: true });

export const AdminUsageSummaryResponseSchema = z.object({
  summary: UsageSummarySchema,
  activity: ActivityCountsSchema,
  byUser: z.array(UsageByUserRowSchema),
  byAccount: z.array(UsageByAccountRowSchema),
  reconciliation: z.object({
    lastRunAt: z.string().nullable(),
    lastResult: z.enum(['succeeded', 'failed', 'credential_unavailable', 'never']),
    pendingRecords: CountSchema,
  }),
  // added by server: the window the summary, rollups and activity counts cover. `from` is null when
  // the caller omitted it (all time), so the UI can label the cards.
  range: z.object({ from: IsoDateTimeSchema.nullable(), to: IsoDateTimeSchema }),
  // added by server: true when at least one usage record falls inside `range` (and the user/account
  // filters). With no usage the UI shows `--` for cost, not $0; a zero estimate only means
  // something once usage was recorded.
  hasUsage: z.boolean(),
});
export type AdminUsageSummaryResponse = z.infer<typeof AdminUsageSummaryResponseSchema>;

export const AdminUsageExportRequestSchema = z
  .object({ format: ExportFormatSchema, filters: AdminUsageQuerySchema.omit({ cursor: true, limit: true }), timezone: z.string().min(1).max(64) })
  .strict();

const usageRead = ['audit.read', 'ai_key.manage'] as const;

// added by server-admin: "Reconcile now" trigger (spec section 13 Usage view). Runs the provider usage
// reconciliation for the active keys (or one key) over a bounded period and returns per-key outcomes.
export const AdminUsageReconcileRequestSchema = z
  .object({
    aiKeyId: AiKeyIdSchema.optional(),
    /** Period start; defaults to 30 days before `to`. */
    from: IsoDateTimeSchema.optional(),
    /** Period end; defaults to now. */
    to: IsoDateTimeSchema.optional(),
  })
  .strict();
export type AdminUsageReconcileRequest = z.infer<typeof AdminUsageReconcileRequestSchema>;

export const AdminUsageReconcileResponseSchema = z.object({
  runAt: IsoDateTimeSchema,
  periodStart: IsoDateTimeSchema,
  periodEnd: IsoDateTimeSchema,
  results: z.array(
    z.object({
      aiKeyId: AiKeyIdSchema,
      state: z.enum(['reconciled', 'unavailable', 'failed']),
      recordCount: CountSchema,
    }),
  ),
});
export type AdminUsageReconcileResponse = z.infer<typeof AdminUsageReconcileResponseSchema>;

export const adminUsageRoutes = {
  'admin.usage.query': defineRoute({ id: 'admin.usage.query', method: 'GET', path: '/admin/v1/usage', auth: 'admin', capability: usageRead, requestSchema: AdminUsageQuerySchema, responseSchema: UsagePageSchema, description: 'Usage records with filters and summary.' }),
  'admin.usage.summary': defineRoute({ id: 'admin.usage.summary', method: 'GET', path: '/admin/v1/usage/summary', auth: 'admin', capability: usageRead, requestSchema: AdminUsageSummaryQuerySchema, responseSchema: AdminUsageSummaryResponseSchema, description: 'Spend and activity summary by user and account; unknown costs shown as unknown.' }),
  'admin.usage.export': defineRoute({ id: 'admin.usage.export', method: 'POST', path: '/admin/v1/usage/exports', auth: 'admin', capability: 'audit.export', requestSchema: AdminUsageExportRequestSchema, responseSchema: ExportResultSchema, description: 'Export usage (CSV/JSON); records a usage.exported event.' }),
  // added by server-admin
  'admin.usage.reconcile': defineRoute({ id: 'admin.usage.reconcile', method: 'POST', path: '/admin/v1/usage/reconcile', auth: 'admin', capability: 'ai_key.manage', requestSchema: AdminUsageReconcileRequestSchema, responseSchema: AdminUsageReconcileResponseSchema, description: 'Reconcile now: run provider usage reconciliation for active keys over a period; records ops.usage_reconciled per key.' }),
} as const;
